import { FIELD_RADIUS, SPARKS_PER_STAR } from '@star-snake/shared'
import { NeonColors, TOP5_COLORS } from './neon-theme'
import { getAppViewport, mountToAppShell } from './appShell'

const SAFE_TOP = 35
const RING_MARGIN = 16
const SCALE_FACTOR = 2.0
const TICK_COUNT = 60
const TICK_LENGTH = 10
const TICK_WIDTH = 2

interface MinimapSnake {
  entityId: number
  x: number
  z: number
}

interface DecodedMinimap {
  tick?: number
  snakes: MinimapSnake[]
  portalActive: boolean
  portalInRange: boolean
  portalX: number
  portalZ: number
  portalAngle: number
}

/** Score label data passed from HUD for rendering on the minimap. */
interface ScoreLabel {
  entityId: number
  score: number
  color: string
  isMe: boolean
}

const MINIMAP_POS_KEY = 'star-snake-minimap-pos'
const MINIMAP_ZOOM_KEY = 'star-snake-minimap-zoom'

function formatMinimapScore(score: number): { text: string; asStars: boolean } {
  const sparks = Math.max(0, Math.floor(score))
  if (sparks < 1000) {
    return { text: String(sparks), asStars: false }
  }
  return { text: String(Math.floor(sparks / SPARKS_PER_STAR)), asStars: true }
}

export class Minimap {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private size: number
  private canvasSize: number
  private scale: number
  private fieldRadius: number
  private resizeHandler: () => void
  private touchStartHandler: ((e: TouchEvent) => void) | null = null
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null
  private touchEndHandler: ((e: TouchEvent) => void) | null = null
  private mouseDownHandler: ((e: MouseEvent) => void) | null = null
  private mouseMoveHandler: ((e: MouseEvent) => void) | null = null
  private mouseUpHandler: (() => void) | null = null
  private readonly labelMap = new Map<number, ScoreLabel>()

  // Drag state
  private posX: number
  private posY: number
  private isDragging = false
  private dragStartX = 0
  private dragStartY = 0
  private dragOffsetX = 0
  private dragOffsetY = 0

  // Pinch zoom state
  private zoomLevel = 1.0
  private pinchStartDist = 0
  private pinchStartZoom = 1.0

  constructor(fieldRadius: number = FIELD_RADIUS) {
    this.fieldRadius = fieldRadius

    // Load saved position/zoom
    const savedPos = this.loadPosition()
    this.posX = savedPos.x
    this.posY = savedPos.y
    this.zoomLevel = this.loadZoom()

    this.size = Math.round((this.getBaseSize() * 0.7) * this.zoomLevel)
    this.canvasSize = this.size + RING_MARGIN * 2

    this.canvas = document.createElement('canvas')
    this.canvas.width = this.canvasSize
    this.canvas.height = this.canvasSize
    this.applyCanvasStyle()

    const ctx = this.canvas.getContext('2d')
    if (!ctx) throw new Error('Could not get 2d context')
    this.ctx = ctx

    this.scale = this.size / (this.fieldRadius * SCALE_FACTOR)

    mountToAppShell(this.canvas)

    this.resizeHandler = () => this.handleResize()
    window.addEventListener('resize', this.resizeHandler)
    this.setupDragAndPinch()
  }

  private loadPosition(): { x: number; y: number } {
    const viewport = getAppViewport()
    try {
      const saved = localStorage.getItem(MINIMAP_POS_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          return parsed
        }
      }
    } catch { /* ignore */ }
    // Default: top-center
    return {
      x: Math.round((viewport.width - (Math.round(this.getBaseSize() * 0.7) + RING_MARGIN * 2)) / 2),
      y: SAFE_TOP,
    }
  }

  private getBaseSize(): number {
    return getAppViewport().width < 768 ? 240 : 300
  }

  private loadZoom(): number {
    try {
      const saved = localStorage.getItem(MINIMAP_ZOOM_KEY)
      if (saved) {
        const val = parseFloat(saved)
        if (val >= 0.5 && val <= 2.0) return val
      }
    } catch { /* ignore */ }
    return 1.0
  }

  private savePosition(): void {
    localStorage.setItem(MINIMAP_POS_KEY, JSON.stringify({ x: this.posX, y: this.posY }))
  }

  private saveZoom(): void {
    localStorage.setItem(MINIMAP_ZOOM_KEY, String(this.zoomLevel))
  }

  private setupDragAndPinch(): void {
    this.touchStartHandler = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        this.isDragging = true
        this.dragStartX = e.touches[0].clientX
        this.dragStartY = e.touches[0].clientY
        this.dragOffsetX = this.posX
        this.dragOffsetY = this.posY
        e.preventDefault()
      } else if (e.touches.length === 2) {
        this.isDragging = false
        this.pinchStartDist = this.getTouchDistance(e.touches)
        this.pinchStartZoom = this.zoomLevel
        e.preventDefault()
      }
    }
    this.canvas.addEventListener('touchstart', this.touchStartHandler, { passive: false })

    this.touchMoveHandler = (e: TouchEvent) => {
      if (e.touches.length === 1 && this.isDragging) {
        this.posX = this.dragOffsetX + (e.touches[0].clientX - this.dragStartX)
        this.posY = this.dragOffsetY + (e.touches[0].clientY - this.dragStartY)
        this.clampPosition()
        this.applyCanvasStyle()
        e.preventDefault()
      } else if (e.touches.length === 2) {
        const dist = this.getTouchDistance(e.touches)
        const ratio = dist / this.pinchStartDist
        this.zoomLevel = Math.max(0.5, Math.min(2.0, this.pinchStartZoom * ratio))
        this.rebuildSize()
        e.preventDefault()
      }
    }
    this.canvas.addEventListener('touchmove', this.touchMoveHandler, { passive: false })

    this.touchEndHandler = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        if (this.isDragging) {
          this.isDragging = false
          this.savePosition()
        }
        this.saveZoom()
      }
    }
    this.canvas.addEventListener('touchend', this.touchEndHandler)

    // Mouse drag (for desktop testing)
    this.mouseDownHandler = (e: MouseEvent) => {
      this.isDragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragOffsetX = this.posX
      this.dragOffsetY = this.posY
      e.preventDefault()
    }
    this.canvas.addEventListener('mousedown', this.mouseDownHandler)

    this.mouseMoveHandler = (e: MouseEvent): void => {
      if (!this.isDragging) return
      this.posX = this.dragOffsetX + (e.clientX - this.dragStartX)
      this.posY = this.dragOffsetY + (e.clientY - this.dragStartY)
      this.clampPosition()
      this.applyCanvasStyle()
    }

    this.mouseUpHandler = (): void => {
      if (this.isDragging) {
        this.isDragging = false
        this.savePosition()
      }
    }

    window.addEventListener('mousemove', this.mouseMoveHandler)
    window.addEventListener('mouseup', this.mouseUpHandler)
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  private clampPosition(): void {
    const viewport = getAppViewport()
    this.posX = Math.max(-this.canvasSize / 2, Math.min(viewport.width - this.canvasSize / 2, this.posX))
    this.posY = Math.max(0, Math.min(viewport.height - this.canvasSize / 2, this.posY))
  }

  private rebuildSize(): void {
    this.size = Math.round((this.getBaseSize() * 0.7) * this.zoomLevel)
    this.canvasSize = this.size + RING_MARGIN * 2
    this.canvas.width = this.canvasSize
    this.canvas.height = this.canvasSize
    this.scale = this.size / (this.fieldRadius * SCALE_FACTOR)
    this.clampPosition()
    this.applyCanvasStyle()
  }

  private applyCanvasStyle(): void {
    this.canvas.style.cssText = `
      position: absolute; top: ${this.posY}px; left: ${this.posX}px;
      width: ${this.canvasSize}px; height: ${this.canvasSize}px;
      border-radius: 50%;
      border: none;
      background: rgba(13,13,26,0.7);
      z-index: 100;
      opacity: 0.8;
      touch-action: none;
    `
  }

  private handleResize(): void {
    const newSize = Math.round((this.getBaseSize() * 0.7) * this.zoomLevel)
    if (newSize !== this.size) {
      this.size = newSize
      this.canvasSize = this.size + RING_MARGIN * 2
      this.canvas.width = this.canvasSize
      this.canvas.height = this.canvasSize
      this.scale = this.size / (this.fieldRadius * SCALE_FACTOR)
      this.clampPosition()
      this.applyCanvasStyle()
    }
  }

  updateFromServer(
    minimapData: DecodedMinimap,
    myX: number,
    myZ: number,
    fieldRadius: number,
    scoreLabels: ScoreLabel[],
    myScore: number,
    timer: number,
    maxDuration: number,
  ): void {
    const ctx = this.ctx
    const center = this.canvasSize / 2
    const scale = this.size / (fieldRadius * SCALE_FACTOR)

    ctx.clearRect(0, 0, this.canvasSize, this.canvasSize)

    // Build lookup: entityId → scoreLabel for fast access
    const labelMap = this.labelMap
    labelMap.clear()
    for (const label of scoreLabels) {
      labelMap.set(label.entityId, label)
    }

    // Snake score labels (non-player snakes)
    ctx.font = 'bold 9px Inter, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    for (const snake of minimapData.snakes) {
      const sx = center + snake.x * scale
      const sz = center + snake.z * scale
      const label = labelMap.get(snake.entityId)

      if (label) {
        ctx.globalAlpha = 1.0
        this.drawScoreLabel(ctx, sx, sz, label.score, label.color, 9)
      } else {
        ctx.beginPath()
        ctx.arc(sx, sz, 2, 0, Math.PI * 2)
        ctx.fillStyle = NeonColors.textBright
        ctx.globalAlpha = 0.3
        ctx.fill()
      }
    }
    ctx.globalAlpha = 1.0

    // Own position — bright white pulsing score number
    const mySx = center + myX * scale
    const mySz = center + myZ * scale
    const pulseAlpha = 0.85 + Math.sin(Date.now() * 0.003) * 0.15

    ctx.font = 'bold 10px Inter, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.globalAlpha = pulseAlpha
    ctx.shadowColor = '#FFFFFF'
    ctx.shadowBlur = 12
    this.drawScoreLabel(ctx, mySx, mySz, myScore, '#FFFFFF', 10)
    this.drawScoreLabel(ctx, mySx, mySz, myScore, '#FFFFFF', 10)
    ctx.shadowBlur = 0
    ctx.globalAlpha = 1.0

    // Portal — always show as vortex at actual position on minimap
    if (minimapData.portalActive) {
      const px = center + minimapData.portalX * scale
      const pz = center + minimapData.portalZ * scale
      const t = Date.now() * 0.003
      const breathe = Math.sin(t * 1.5) * 0.5 + 0.5

      ctx.beginPath()
      ctx.arc(px, pz, 9 + breathe * 2, 0, Math.PI * 2)
      ctx.fillStyle = NeonColors.portal
      ctx.globalAlpha = 0.08 + breathe * 0.06
      ctx.fill()

      const arms = 3
      const spiralSteps = 20
      for (let arm = 0; arm < arms; arm++) {
        const armOffset = (arm * Math.PI * 2) / arms
        ctx.beginPath()
        for (let s = 0; s <= spiralSteps; s++) {
          const frac = s / spiralSteps
          const r = 8 * (1 - frac * 0.85)
          const angle = armOffset + t * 2 + frac * Math.PI * 2.5
          const sx = px + Math.cos(angle) * r
          const sz = pz + Math.sin(angle) * r
          if (s === 0) ctx.moveTo(sx, sz)
          else ctx.lineTo(sx, sz)
        }
        ctx.strokeStyle = NeonColors.portal
        ctx.globalAlpha = 0.5 + breathe * 0.3
        ctx.lineWidth = 1.5 - breathe * 0.3
        ctx.stroke()
      }

      ctx.beginPath()
      ctx.arc(px, pz, 2, 0, Math.PI * 2)
      ctx.fillStyle = '#FFFFFF'
      ctx.globalAlpha = 0.6 + breathe * 0.3
      ctx.shadowColor = NeonColors.portal
      ctx.shadowBlur = 6
      ctx.fill()
      ctx.shadowBlur = 0
      ctx.globalAlpha = 1.0
    }

    // Clock tick ring
    this.drawTickRing(timer, maxDuration)

    // Red border flash in last 5 minutes
    this.drawDangerFlash(timer, maxDuration)
  }

  /**
   * Draw 60 radial tick marks around the minimap like a clock.
   * Each tick = 1 minute. Ticks burn clockwise like a fuse:
   * - Burned portion: dim gray
   * - Unburned portion: bright active color
   * - Burn point: bright white blinking 1Hz
   */
  private drawTickRing(timer: number, maxDuration: number): void {
    const ctx = this.ctx
    const center = this.canvasSize / 2
    const innerR = this.size / 2 + 2
    const outerR = innerR + TICK_LENGTH

    const remaining = Math.max(0, maxDuration - timer)
    const elapsed = maxDuration - remaining
    const elapsedMinutes = elapsed / 60
    const burnedCount = Math.floor(elapsedMinutes)
    const burnFraction = elapsedMinutes - burnedCount
    const isLastMinute = remaining <= 60
    const lastMinutePulse = 0.4 + Math.sin(Date.now() * 0.008) * 0.6
    const remainingMinutes = remaining / 60

    for (let i = 0; i < TICK_COUNT; i++) {
      // Clockwise from 12 o'clock
      const angle = (i / TICK_COUNT) * Math.PI * 2 - Math.PI / 2

      const cosA = Math.cos(angle)
      const sinA = Math.sin(angle)
      const x1 = center + cosA * innerR
      const y1 = center + sinA * innerR
      const x2 = center + cosA * outerR
      const y2 = center + sinA * outerR

      if (i < burnedCount) {
        // Fully burned — dim gray
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = '#444455'
        ctx.globalAlpha = 0.3
        ctx.lineWidth = TICK_WIDTH
        ctx.stroke()
      } else if (i === burnedCount) {
        // Currently burning tick — burns from outer toward inner
        // burnFraction=0 means just started (all active), burnFraction→1 means almost done (all burned)
        const midX = x2 + (x1 - x2) * burnFraction
        const midY = y2 + (y1 - y2) * burnFraction

        // Burned part (outer to mid)
        if (burnFraction > 0.01) {
          ctx.beginPath()
          ctx.moveTo(x2, y2)
          ctx.lineTo(midX, midY)
          ctx.strokeStyle = '#444455'
          ctx.globalAlpha = 0.3
          ctx.lineWidth = TICK_WIDTH
          ctx.stroke()
        }

        // Unburned part (mid to inner)
        if (burnFraction < 0.99) {
          ctx.beginPath()
          ctx.moveTo(midX, midY)
          ctx.lineTo(x1, y1)
          ctx.strokeStyle = this.getActiveTickColor(remainingMinutes)
          ctx.globalAlpha = isLastMinute ? lastMinutePulse : 0.9
          ctx.lineWidth = TICK_WIDTH
          ctx.stroke()
        }

        // Burn point — bright white blinking 1Hz, same width as tick
        const blink = Math.abs(Math.sin(Date.now() * 0.00314))
        ctx.beginPath()
        // Short perpendicular line segment at burn point (same width as tick)
        const perpLen = TICK_WIDTH / 2
        const perpX = -sinA * perpLen
        const perpY = cosA * perpLen
        ctx.moveTo(midX - perpX, midY - perpY)
        ctx.lineTo(midX + perpX, midY + perpY)
        ctx.strokeStyle = '#FFFFFF'
        ctx.globalAlpha = 0.5 + blink * 0.5
        ctx.lineWidth = TICK_WIDTH
        ctx.shadowColor = '#FFFFFF'
        ctx.shadowBlur = 4
        ctx.stroke()
        ctx.shadowBlur = 0
      } else {
        // Future tick — bright active color
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = this.getActiveTickColor(remainingMinutes)
        ctx.globalAlpha = isLastMinute ? lastMinutePulse : 0.9
        ctx.lineWidth = TICK_WIDTH
        ctx.stroke()
      }
    }

    ctx.globalAlpha = 1.0
  }

  private getActiveTickColor(remainingMinutes: number): string {
    if (remainingMinutes > 15) return '#DDDDEE'
    if (remainingMinutes > 10) return '#FFDD44'
    if (remainingMinutes > 5) return '#FF8800'
    return NeonColors.danger
  }

  /**
   * Red border flash with escalating frequency in the last 5 minutes.
   * Each flash is a short red ring stroke that decays over ~0.3s.
   */
  private drawDangerFlash(timer: number, maxDuration: number): void {
    const remaining = Math.max(0, maxDuration - timer)
    if (remaining > 300) return // Only active in last 5 minutes

    // Determine flash interval (seconds) based on remaining time
    let interval: number
    if (remaining > 240) interval = 30       // 5:00–4:00 → every 30s
    else if (remaining > 180) interval = 20  // 4:00–3:00 → every 20s
    else if (remaining > 120) interval = 15  // 3:00–2:00 → every 15s
    else if (remaining > 60) interval = 10   // 2:00–1:00 → every 10s
    else if (remaining > 30) interval = 5    // 1:00–0:30 → every 5s
    else if (remaining > 10) interval = 2    // 0:30–0:10 → every 2s
    else if (remaining > 5) interval = 1     // 0:10–0:05 → every 1s
    else interval = 0.5                      // 0:05–0:00 → every 0.5s

    // Flash fires when remaining crosses an interval boundary (remaining = N*interval)
    // cyclePos = how far remaining is past the last boundary (counting down)
    const cyclePos = remaining % interval
    // flashAge = time elapsed since last flash (cyclePos=0 means flash just fired)
    const flashAge = cyclePos < 0.001 ? 0 : interval - cyclePos
    const flashDuration = 0.3
    if (flashAge > flashDuration) return

    const alpha = 1 - flashAge / flashDuration // 1→0 over 0.3s
    const ctx = this.ctx
    const center = this.canvasSize / 2
    const r = this.size / 2

    ctx.beginPath()
    ctx.arc(center, center, r, 0, Math.PI * 2)
    ctx.strokeStyle = NeonColors.danger
    ctx.globalAlpha = alpha * 0.7
    ctx.lineWidth = 3
    ctx.shadowColor = NeonColors.danger
    ctx.shadowBlur = 8
    ctx.stroke()
    ctx.shadowBlur = 0
    ctx.globalAlpha = 1
  }

  private drawScoreLabel(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    score: number,
    color: string,
    fontSize: number,
  ): void {
    const formatted = formatMinimapScore(score)
    ctx.font = `bold ${fontSize}px Inter, sans-serif`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'

    if (!formatted.asStars) {
      const textWidth = ctx.measureText(formatted.text).width
      ctx.fillStyle = color
      ctx.fillText(formatted.text, x - textWidth / 2, y)
      return
    }

    const starGap = 2
    const numberWidth = ctx.measureText(formatted.text).width
    const starSize = fontSize + 1
    const starWidth = starSize
    const totalWidth = numberWidth + starGap + starWidth
    const startX = x - totalWidth / 2

    ctx.fillStyle = color
    ctx.fillText(formatted.text, startX, y)
    this.drawStarIcon(
      ctx,
      startX + numberWidth + starGap + starWidth / 2,
      y,
      starSize / 2,
      NeonColors.gold,
    )
  }

  private drawStarIcon(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    fill: string,
  ): void {
    const innerRadius = radius * 0.5
    ctx.beginPath()
    for (let i = 0; i < 10; i++) {
      const angle = -Math.PI / 2 + i * (Math.PI / 5)
      const pointRadius = i % 2 === 0 ? radius : innerRadius
      const px = x + Math.cos(angle) * pointRadius
      const py = y + Math.sin(angle) * pointRadius
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.closePath()
    ctx.fillStyle = fill
    ctx.shadowColor = fill
    ctx.shadowBlur = radius * 0.65
    ctx.fill()
    ctx.shadowBlur = 0
  }

  dispose(): void {
    window.removeEventListener('resize', this.resizeHandler)
    if (this.touchStartHandler) this.canvas.removeEventListener('touchstart', this.touchStartHandler)
    if (this.touchMoveHandler) this.canvas.removeEventListener('touchmove', this.touchMoveHandler)
    if (this.touchEndHandler) this.canvas.removeEventListener('touchend', this.touchEndHandler)
    if (this.mouseDownHandler) this.canvas.removeEventListener('mousedown', this.mouseDownHandler)
    if (this.mouseMoveHandler) window.removeEventListener('mousemove', this.mouseMoveHandler)
    if (this.mouseUpHandler) window.removeEventListener('mouseup', this.mouseUpHandler)
    this.canvas.remove()
  }
}
